<script lang="ts">
	import { createEventDispatcher } from 'svelte';
	import { field } from 'svelte-forms';
	import { getTranslate } from '@tolgee/svelte';

	import InputWithLabel from '$lib/components/Form/Input/InputWithLabel.svelte';
	import { customForm } from '$lib/types/form';
	import Button from '../Button/Button.svelte';
	import { logInWithEmail } from '$lib/service/supabase/user.service';
	import { classes } from '$lib/utils';

	import { required } from 'svelte-forms/validators';
	import type { User } from '@supabase/supabase-js';
	import { goto } from '$app/navigation';
	import { SIGN_UP_STEP } from '$lib/constants';
	import { profileApi } from '$lib/service/supabase/profile.api';
	import { redirectOnUserMetaDataMissing } from '$lib/service/loginRedirects';

	const { t } = getTranslate();
	const dispatch = createEventDispatcher();

	export let additionalClass = '';

	let loading = false;

	const email = field('email', '', [required()]);
	const password = field('password', '', [required()]);
	const logInForm = customForm(email, password);
	let serverError: string = '';

	const handleSubmit = async () => {
		loading = true;
		await logInForm.internalForm.validate();

		try {
			// TODO: form validation etc. - additionally communicate when the credentials are wrong
			if (!$logInForm.valid) {
				throw 'login form invalid';
			}
			const { user } = await logInWithEmail($email.value, $password.value);
			let userIsMissingMetaData = false;

			if (user) {
				userIsMissingMetaData = await redirectOnUserMetaDataMissing(user);
			}

			if (!userIsMissingMetaData) {
				dispatch('success', user);
			}
		} catch (error: any) {
			console.error('ERROR while logging in', error);
			const { message } = error;
			serverError = message;
			dispatch('error', error);
		} finally {
			loading = false;
		}
	};
</script>

<div class={classes(additionalClass, 'flex flex-col items-center justify-center')}>
	<h1 class="mb-8 text-xl font-semibold w-full">{@html $t('login.headline')}</h1>
	<form
		class="space-y-4 lg:space-y-6 w-full flex flex-col justify-center"
		on:submit|preventDefault={handleSubmit}
	>
		<InputWithLabel
			type="email"
			id="email"
			data-test="__login-email"
			placeholder="E-Mail"
			bind:value={$email.value}
			errorClass="border-error"
			errorMessage={$t('form_emailRequiredErrorMessage')}
			hasError={$logInForm.hasError('email.required') ||
				($logInForm.hasError('email.match_field') && !$logInForm.hasError('email.required'))}
		/>
		<InputWithLabel
			type="password"
			id="password"
			data-test="__login-password"
			placeholder="Password"
			bind:value={$password.value}
			errorClass="border-error"
			errorMessage={$t('form_passwordRequiredErrorMessage')}
			hasError={$logInForm.hasError('password.required') ||
				($logInForm.hasError('password.match_field') && !$logInForm.hasError('password.required'))}
		/>

		<a class="text-xs text-purple hover:text-purple-hover" href="/auth/forgot-password"
			>{@html $t('login.forgot_password_link')}</a
		>

		{#if serverError}
			<div class="text-error">{serverError}</div>
		{/if}

		<div class="text-center">
			<Button
				data-test="__login-submit"
				additionalClass="mt-4 w-full text-white bg-black"
				type="submit">{$t('login.button')}</Button
			>
			<p class="text-sm font-normal mt-4">
				{@html $t('login.no_account_yet_01')}
				<a href="/auth/signup" class="font-medium hover:underline text-purple"
					>{@html $t('login.no_account_yet_02')}</a
				>
			</p>
		</div>
	</form>
</div>
